import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Link, MessageCircle, Send } from 'lucide-react';

import { withShareUtm, useShareText, shareContentFromUrl, ShareMedium } from '../../utils/share';
import { trackShare } from '../../hooks/useAnalytics';

interface SharePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement>;
  url?: string;
  title?: string;
  text?: string;
}

export const SharePopover: React.FC<SharePopoverProps> = ({
  isOpen,
  onClose,
  anchorRef,
  url = window.location.href,
  title: _title = 'Caramba!',
  text: textProp,
}) => {
  const pageText = useShareText();
  const text = textProp ?? pageText;
  // content_shared: se dispara al elegir el canal (no sabemos si la persona termina enviando)
  const track = (medium: ShareMedium) => {
    const { type, id } = shareContentFromUrl(url);
    trackShare(type, id, medium);
  };
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {onClose();}
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        // Check if click is not on the anchor button
        if (anchorRef?.current && !anchorRef.current.contains(e.target as Node)) {
          onClose();
        }
      }
    };

    if (isOpen) {
      // Small delay to prevent immediate close on open
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, anchorRef]);

  const shareOptions = [
    {
      name: 'X (Twitter)',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      color: 'hover:bg-gray-100',
      action: () => {
        track('x');
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(withShareUtm(url, 'x'))}`;
        window.open(twitterUrl, '_blank', 'width=550,height=450');
      },
    },
    {
      name: 'WhatsApp',
      icon: <MessageCircle className="w-5 h-5" />,
      color: 'hover:bg-green-50',
      action: () => {
        track('whatsapp');
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${text} ${withShareUtm(url, 'whatsapp')}`)}`;
        window.open(whatsappUrl, '_blank');
      },
    },
    {
      name: 'Telegram',
      icon: <Send className="w-5 h-5" />,
      color: 'hover:bg-blue-50',
      action: () => {
        track('telegram');
        const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(withShareUtm(url, 'telegram'))}&text=${encodeURIComponent(text)}`;
        window.open(telegramUrl, '_blank');
      },
    },
    {
      name: 'Facebook',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      color: 'hover:bg-blue-50',
      action: () => {
        track('facebook');
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(withShareUtm(url, 'facebook'))}`;
        window.open(facebookUrl, '_blank', 'width=550,height=450');
      },
    },
    {
      name: 'LinkedIn',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
      color: 'hover:bg-blue-50',
      action: () => {
        track('linkedin');
        const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(withShareUtm(url, 'linkedin'))}`;
        window.open(linkedinUrl, '_blank', 'width=550,height=450');
      },
    },
    {
      name: 'Copiar enlace',
      icon: <Link className="w-5 h-5" />,
      color: 'hover:bg-gray-50',
      action: async () => {
        track('copy');
        try {
          await navigator.clipboard.writeText(withShareUtm(url, 'copy'));
          // You might want to show a toast notification here
          alert('Enlace copiado al portapapeles');
        } catch (err) {
          console.error('Error al copiar:', err);
        }
      },
    },
  ];

  // Calculate position based on anchor
  const getPosition = () => {
    if (!anchorRef?.current) {return {};}

    const rect = anchorRef.current.getBoundingClientRect();
    const popoverWidth = 400;
    const popoverHeight = 380;

    // Position to the left of the button
    const left = rect.left - popoverWidth - 12;
    // Center vertically with the button, but adjust if too close to edges
    let top = rect.top + (rect.height / 2) - (popoverHeight / 2);

    // Adjust if too close to top/bottom
    if (top < 20) {top = 20;}
    if (top + popoverHeight > window.innerHeight - 20) {
      top = window.innerHeight - popoverHeight - 20;
    }

    return {
      position: 'fixed' as const,
      left: `${left}px`,
      top: `${top}px`,
      zIndex: 50,
    };
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={popoverRef}
          initial={{ opacity: 0, scale: 0.95, x: 10 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{ opacity: 0, scale: 0.95, x: 10 }}
          style={getPosition()}
          className="w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-gray-700" />
                <h3 className="text-lg font-semibold">Compartir</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Comparte Caramba! con tus contactos
            </p>

            <div className="grid grid-cols-3 gap-3">
              {shareOptions.map((option) => (
                <button
                  key={option.name}
                  onClick={() => {
                    option.action();
                    if (option.name !== 'Copiar enlace') {
                      onClose();
                    }
                  }}
                  className={`flex flex-col items-center justify-center p-4 rounded-lg border border-gray-200 transition-colors ${option.color}`}
                >
                  <div className="text-gray-700 mb-2">{option.icon}</div>
                  <span className="text-xs text-gray-600">{option.name}</span>
                </button>
              ))}
            </div>

            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 font-mono truncate">{url}</p>
            </div>
          </div>

          {/* Arrow pointing to button */}
          <div className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-white" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};